import { useTrackStorefrontImpression } from '@hooks/useTrackStorefrontImpression';
import { TextTitle } from '@tkww/orion-web-ds-text';
import React, { type FC, useMemo } from 'react';
import { Amenities } from './components/amenities/Amenities';
import { Detail } from './components/detail/Detail';

import { useAppSelector } from '@redux/hooks';
import type { Decorated } from 'types/vendor';
import type { NavItemProps } from '../nav/types';
import Styles from './styles.scss';

import { vendorAwardsAtom } from '@client/jotai/storefront/awards-summary';
import { AwardsBanner } from '@components/awards-banner/awards-banner';
import { HighlightedRequest } from '@components/highlightedRequest/highlightedRequest';
import { useStorefrontEmptyStates } from '@feature-flags/hooks/Storefront/useStorefrontEmptyStates';
import { useLiteStorefront } from '@hooks/use-lite-storefront';
import { useAtomValue } from 'jotai';

interface HeaderProps {
	vendor: Decorated;
}

const Header = ({ vendor }: HeaderProps) => {
	const detailsTrackingRef = useTrackStorefrontImpression('What we offer');

	const text = useMemo(() => {
		if (vendor.categoryCode === 'REC') {
			return 'Amenities + details';
		}

		return 'Details';
	}, [vendor]);

	return (
		<TextTitle size="sm" className={Styles.title} ref={detailsTrackingRef}>
			{text}
		</TextTitle>
	);
};

interface ContentProps {
	vendor: Decorated;
}

const Content = ({ vendor }: ContentProps) => {
	const isEmptyState = useStorefrontEmptyStates();

	const venueAmenities = useAppSelector(
		(state) => state.settings.venueAmenities,
	);

	return (
		<div className={Styles.contentContainer}>
			<Amenities
				vendor={vendor}
				venueAmenities={venueAmenities}
				shouldShowEmptyState={isEmptyState}
			/>
			<Detail vendor={vendor} />
		</div>
	);
};

export const Details: FC<NavItemProps> = () => {
	const vendor = useAppSelector(
		(state) => state.vendor.vendor as unknown as Decorated,
	);

	const isLiteStorefront = useLiteStorefront();
	const awards = useAtomValue(vendorAwardsAtom);
	const hasAwards = awards.length > 0;

	return (
		<div>
			<Header vendor={vendor} />
			<Content vendor={vendor} />
			{!isLiteStorefront &&
				(hasAwards ? (
					<AwardsBanner />
				) : (
					<HighlightedRequest
						messageText="Any questions?"
						buttonText="Start a conversation"
						initiator="Details CTA"
						hideIfLead={true}
					/>
				))}
		</div>
	);
};
